import PropTypes from 'prop-types';
import { and, equals, includes, pluck, prop, propOr, reject } from 'ramda';
import {
  formatZipCode,
  isRequiredBoolean,
  isRequiredInput,
  isRequiredSelect,
  nameIsValid,
  numberIsBetween,
  zipCodeRules
} from '@@viewHelpers/validations';
import {
  ControlledDatePicker,
  ControlledInput,
  ControlledRadioButton,
  ControlledSelect,
  ControlledSocialSecurityOrganismCode,
  ControlledSSInput,
  ControlledSwitch
} from '@@viewCommonComponents/controlled';
import { DashedTitle, Message } from '@fasstech/middle-design-system';
import { FormLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import booleanOptions, {
  civilityOptions,
  getCaisseFromValue
} from '@@viewCommonComponents/forms/options/index.js';
import TwinRanksSelect from '@@viewCommonComponents/TwinRanksSelect.jsx';
import useSouscriptionCustomerInformations from '../../hooks/useSouscriptionCustomerInformations.js';
import SouscriptionCustomerGranteesExtraForm from '../../extra/SouscriptionCustomerGranteesExtraForm.jsx';
import CustomerIsCSS from '../../helpers/CustomerIsCSS.jsx';
import SouscriptionCustomerExtraForm from '../../extra/SouscriptionCustomerExtraForm.jsx';
import useBirthDate from '../../../../simulationCustomerGrantees/client/components/CustomerForm/hooks/useBirthDate.jsx';
import SouscriptionCustomerGranteesExtraCompanyInformation from '../../extra/SouscriptionCustomerGranteesExtraCompanyInformation.jsx';

const SouscriptionCustomerForm = ({
  getMiddleBoolean,
  getMiddleNumber,
  professionalSituations,
  socioProfessionalCategories,
  activities,
  regimeObligatoire,
  jobs = [],
  selectedTab,
  legalIndicators,
  familySituations,
  getMiddleText,
  getCountries,
  madelinMonths,
  project
}) => {
  const {
    isDigitalContractOptions,
    birthCountryIsFrance,
    cities,
    form,
    customerIsTns,
    displayMadelinMonth,
    displayGeneralView
  } = useSouscriptionCustomerInformations({
    getMiddleBoolean,
    jobs
  });
  const effectDate = form.watch('offerEffectDate');

  const { minBirthDate, maxBirthDate } = useBirthDate({
    getMiddleNumber,
    getMiddleText,
    effectDate
  });

  const excludedCaissesPro = pluck('CAISSES')(
    getMiddleText('ADHERENT_CAISSES_PRO_EXCLUES')
  );

  const filteredJobs = reject((job) =>
    includes(prop('caisse', job), excludedCaissesPro)
  )(jobs || []);

  const caisseProValue = getCaisseFromValue(
    form.watch('customer.CSPs.0.externalId'),
    filteredJobs || []
  );

  return (
    <FormLayout className={`${selectedTab !== 0 ? 'hidden' : ''}`}>
      <DashedTitle>
        {getMiddleText('ADHERENT_GENERAL_VIEW_SUBTITLE')}
      </DashedTitle>
      <ControlledRadioButton
        name="customer.civility"
        rules={isRequiredSelect(
          civilityOptions,
          getMiddleBoolean('ADHERENT_CIVILITE_EST_OBLIGATOIRE')
        )}
        form={form}
        options={civilityOptions}
        disabled={and(
          getMiddleBoolean('ADHERENT_CIVILITE_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_CIVILITE_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="ADHERENT_CIVILITE"
        isDisplayed={
          getMiddleBoolean('ADHERENT_CIVILITE_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_CIVILITE_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean(
              'ADHERENT_CIVILITE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <ControlledInput
        name="customer.lastName"
        label={getMiddleText('ADHERENT_NOM_LABEL')}
        form={form}
        rules={nameIsValid(getMiddleBoolean('ADHERENT_NOM_EST_OBLIGATOIRE'))}
        disabled={and(
          getMiddleBoolean('ADHERENT_NOM_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_NOM_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="ADHERENT_NOM"
        isDisplayed={
          getMiddleBoolean('ADHERENT_NOM_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_NOM_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('ADHERENT_NOM_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
      />

      <ControlledInput
        name="customer.birthName"
        label={getMiddleText('ADHERENT_NOM_NAISSANCE_LABEL')}
        form={form}
        rules={nameIsValid(
          getMiddleBoolean('ADHERENT_NOM_NAISSANCE_EST_OBLIGATOIRE')
        )}
        data-testid="ADHERENT_NOM_NAISSANCE"
        isDisplayed={getMiddleBoolean('ADHERENT_NOM_NAISSANCE_EST_NECESSAIRE')}
      />

      <ControlledInput
        name="customer.firstName"
        label={getMiddleText('ADHERENT_PRENOM_LABEL')}
        form={form}
        rules={nameIsValid(getMiddleBoolean('ADHERENT_PRENOM_EST_OBLIGATOIRE'))}
        disabled={and(
          getMiddleBoolean('ADHERENT_PRENOM_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_PRENOM_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="ADHERENT_PRENOM"
        isDisplayed={
          getMiddleBoolean('ADHERENT_PRENOM_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_PRENOM_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean(
              'ADHERENT_PRENOM_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <ControlledDatePicker
        name="customer.birthDate"
        label={getMiddleText('ADHERENT_DATE_NAISSANCE_LABEL')}
        form={form}
        minDate={minBirthDate?.toDate()}
        maxDate={maxBirthDate?.toDate()}
        rules={isRequiredInput(
          getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_OBLIGATOIRE')
        )} // TODO check plus tard
        disabled={and(
          getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_OBLIGATOIRE'),
          getMiddleBoolean(
            'ADHERENT_DATE_NAISSANCE_EST_DEMANDE_DONNEES_CONTRAT'
          )
        )}
        data-testid="ADHERENT_DATE_NAISSANCE"
        isDisplayed={
          getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_NECESSAIRE') &&
          (getMiddleBoolean(
            'ADHERENT_DATE_NAISSANCE_EST_DEMANDE_DONNEES_CONTRAT'
          ) ||
            getMiddleBoolean(
              'ADHERENT_DATE_NAISSANCE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <ControlledSelect
        name="customer.birthCountry"
        label={getMiddleText('MEMBER_BIRTHCOUNTRY_LABEL')}
        form={form}
        options={propOr([], 'countries', getCountries)}
        isSearchable
        rules={isRequiredSelect(propOr([], 'countries', getCountries))}
        data-testid="MEMBER_BIRTHCOUNTRY"
        isDisplayed={getMiddleBoolean('DISPLAY_MEMBER_BIRTHCOUNTRY')}
      />

      <ControlledInput
        name="customer.birthZipCode"
        label="CP de la ville de naissance"
        form={form}
        formatValue={formatZipCode}
        rules={zipCodeRules()}
        data-testid="customerBirthZipCode"
        isDisplayed={birthCountryIsFrance}
      />

      <ControlledSelect
        name="customer.birthCity"
        label="Ville de naissance"
        form={form}
        options={cities}
        isSearchable
        rules={isRequiredSelect(cities)}
        data-testid="customerBirthCity"
        isDisplayed={
          birthCountryIsFrance && getMiddleBoolean('DISPLAY_MEMBER_BIRTHCITY')
        }
      />

      <ControlledSelect
        name="customer.maritalStatus"
        label={getMiddleText('MEMBER_FAMILYSITUATION_LABEL')}
        form={form}
        options={familySituations || []}
        rules={isRequiredSelect(familySituations)}
        data-testid="MEMBER_FAMILYSITUATION"
      />

      <ControlledSelect
        name="customer.CSPs.0.categoryLvl1CSPExternalId"
        label={getMiddleText('ADHERENT_SIT_PRO_LABEL')}
        form={form}
        options={professionalSituations || []}
        rules={isRequiredSelect(
          professionalSituations,
          getMiddleBoolean('ADHERENT_SIT_PRO_EST_OBLIGATOIRE')
        )}
        disabled={
          getMiddleBoolean('ADHERENT_SIT_PRO_EST_DEMANDE_DONNEES_CONTRAT') &&
          getMiddleBoolean('ADHERENT_SIT_PRO_EST_OBLIGATOIRE')
        }
        data-testid="ADHERENT_SIT_PRO"
        isDisplayed={
          getMiddleBoolean('ADHERENT_SIT_PRO_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_SIT_PRO_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean(
              'ADHERENT_SIT_PRO_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <ControlledSelect
        name="customer.CSPs.0.categoryCSPExternalId"
        label={getMiddleText('ADHERENT_CSP_LABEL')}
        rules={isRequiredSelect(
          socioProfessionalCategories,
          getMiddleBoolean('ADHERENT_CSP_EST_OBLIGATOIRE')
        )}
        form={form}
        options={socioProfessionalCategories || []}
        trigger={form.trigger}
        disabled={and(
          getMiddleBoolean('ADHERENT_CSP_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_CSP_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="ADHERENT_CSP"
        isDisplayed={
          getMiddleBoolean('ADHERENT_CSP_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_CSP_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('ADHERENT_CSP_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
      />

      <ControlledSelect
        name="customer.CSPs.0.externalId"
        label={getMiddleText('ADHERENT_PROFESSION_LABEL')}
        rules={isRequiredSelect(
          filteredJobs,
          getMiddleBoolean('ADHERENT_PROFESSION_EST_OBLIGATOIRE')
        )}
        form={form}
        options={filteredJobs || []}
        tooltip={getMiddleText('ADHERENT_PROFESSION_INFO')}
        trigger={form.trigger}
        disabled={and(
          getMiddleBoolean('ADHERENT_PROFESSION_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="ADHERENT_PROFESSION"
        isDisplayed={
          getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
          (getMiddleBoolean(
            'ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT'
          ) ||
            getMiddleBoolean(
              'ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <DashedTitle isDisplayed={displayGeneralView}>
        {getMiddleText('ADHERENT_GENERAL_VIEW_SUBTITLE2')}
      </DashedTitle>

      <ControlledInput
        name="customer.socialSecurities.0.regime"
        label={getMiddleText('ADHERENT_CAISSE_PRO_LABEL')}
        form={form}
        rules={isRequiredInput(
          getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_OBLIGATOIRE')
        )}
        value={caisseProValue}
        disabled
        data-testid="ADHERENT_CAISSE_PRO"
        isDisplayed={
          getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
          (getMiddleBoolean(
            'ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT'
          ) ||
            getMiddleBoolean(
              'ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            )) &&
          getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_NECESSAIRE') &&
          (getMiddleBoolean(
            'ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_CONTRAT'
          ) ||
            getMiddleBoolean(
              'ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <ControlledSelect
        name="customer.socialSecurities.0.regime"
        label={getMiddleText('ADHERENT_REGIME_LABEL')}
        form={form}
        options={propOr([], 'regimes', regimeObligatoire)}
        rules={isRequiredSelect(
          propOr([], 'regimes', regimeObligatoire),
          getMiddleBoolean('ADHERENT_REGIME_EST_OBLIGATOIRE')
        )}
        disabled={and(
          getMiddleBoolean('ADHERENT_REGIME_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="ADHERENT_REGIME"
        isDisplayed={
          getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean(
              'ADHERENT_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <ControlledRadioButton
        containerClassName="w-full"
        name="customer.professionalStatus"
        label={getMiddleText('ADHERENT_ACTIVITE_LABEL')}
        rules={isRequiredSelect(
          activities,
          getMiddleBoolean('ADHERENT_ACTIVITE_EST_OBLIGATOIRE')
        )}
        form={form}
        className={'grid gap-[10px]'}
        options={activities}
        disabled={and(
          getMiddleBoolean('ADHERENT_ACTIVITE_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="ADHERENT_ACTIVITE"
        isDisplayed={
          getMiddleBoolean('ADHERENT_ACTIVITE_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean(
              'ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <ControlledRadioButton
        containerClassName="w-full"
        label={getMiddleText('ADHERENT_ID_TNS_LABEL')}
        id="customer.isTNS"
        name="customer.isTNS"
        booleanValue
        form={form}
        rules={isRequiredBoolean(
          getMiddleBoolean('ADHERENT_ID_TNS_EST_OBLIGATOIRE')
        )}
        options={booleanOptions}
        disabled={and(
          getMiddleBoolean('ADHERENT_ID_TNS_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="ADHERENT_ID_TNS_LABEL"
        isDisplayed={
          getMiddleBoolean('ADHERENT_ID_TNS_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean(
              'ADHERENT_ID_TNS_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <CustomerIsCSS
        form={form}
        getMiddleBoolean={getMiddleBoolean}
        getMiddleText={getMiddleText}
      />

      <ControlledSwitch
        label={getMiddleText('CUSTOMER_IS_MADELIN_LABEL')}
        form={form}
        name="customer.isMadelin"
        data-testid="CUSTOMER_IS_MADELIN"
        isDisplayed={customerIsTns}
      />

      <ControlledSelect
        label={getMiddleText('CUSTOMER_MADELIN_MONTH_LABEL')}
        name="customer.madelinMonth"
        form={form}
        options={madelinMonths}
        rules={isRequiredSelect(madelinMonths)}
        data-testid="CUSTOMER_MADELIN_MONTH"
        isDisplayed={customerIsTns && displayMadelinMonth}
      />

      <ControlledSSInput
        label={getMiddleBoolean('ADHERENT_NSS_LABEL')}
        SSInputName="customer.socialSecurities.0.number"
        birthDateInputName="customer.birthDate"
        form={form}
        mandatory={getMiddleBoolean('ADHERENT_NSS_EST_OBLIGATOIRE')}
        disabled={and(
          getMiddleBoolean('ADHERENT_NSS_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_NSS_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        data-testid="ADHERENT_NSS"
        isDisplayed={
          getMiddleBoolean('ADHERENT_NSS_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_NSS_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean('ADHERENT_NSS_EST_DEMANDE_DONNEES_SOUSCRIPTION'))
        }
      />

      <ControlledSocialSecurityOrganismCode
        name="customer.socialSecurities.0.organismCode"
        regimeName="customer.socialSecurities.0.regime"
        rules={isRequiredInput(
          getMiddleBoolean('ADHERENT_ORG_RATT_EST_OBLIGATOIRE')
        )}
        regimeObligatoire={regimeObligatoire}
        getMiddleText={getMiddleText}
        tooltipOrganismCode={getMiddleText('ADHERENT_ORG_RATT_INFO_BULLE')}
        labelOrganismCode={getMiddleText('ADHERENT_ORG_RATT_LABEL')}
        tooltipRegime={getMiddleText('ADHERENT_ORG_RATT_REGIME_INFO_BULLE')}
        tooltipCaisse={getMiddleText('ADHERENT_ORG_RATT_CAISSE_INFO_BULLE')}
        tooltipCentre={getMiddleText('ADHERENT_ORG_RATT_CENTRE_INFO_BULLE')}
        data-testid="ADHERENT_ORG_RATT"
        className={'w-[112%]!'}
        isDisplayed={
          getMiddleBoolean('ADHERENT_ORG_RATT_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT') ||
            getMiddleBoolean(
              'ADHERENT_REGIME_EST_DEMANDE_DONNEES_SOUSCRIPTION'
            ))
        }
      />

      <TwinRanksSelect
        form={form}
        rules={getMiddleBoolean('ADHERENT_RANG_EST_OBLIGATOIRE')}
        name="customer.socialSecurities.0.twinsRank"
        disabled={and(
          getMiddleBoolean('ADHERENT_RANG_EST_OBLIGATOIRE'),
          getMiddleBoolean('ADHERENT_RANG_EST_DEMANDE_DONNEES_CONTRAT')
        )}
        label={getMiddleText('ADHERENT_RANG_LABEL')}
        tooltip={getMiddleText('ADHERENT_RANG_INFO_BULLE')}
        data-testid="ADHERENT_RANG"
        isDisplayed={
          getMiddleBoolean('ADHERENT_RANG_EST_NECESSAIRE') &&
          (getMiddleBoolean('ADHERENT_RANG_EST_DEMANDE_DONNEES_SOUSCRIPTION') ||
            getMiddleBoolean('ADHERENT_RANG_EST_DEMANDE_DONNEES_CONTRAT'))
        }
      />

      <ControlledRadioButton
        label={getMiddleText('ADHERENT_PPE_LABEL')}
        name={'customer.amls.0.isPoliticallyExposed'}
        id={'customer.amls.0.isPoliticallyExposed'}
        form={form}
        rules={{
          ...isRequiredBoolean(
            getMiddleBoolean('ADHERENT_PPE_EST_OBLIGATOIRE')
          )
        }}
        options={booleanOptions}
        data-testid="POLITICALLY_EXPOSED"
        isDisplayed={getMiddleBoolean('ADHERENT_PPE_EST_NECESSAIRE')}
      />

      <SouscriptionCustomerGranteesExtraCompanyInformation form={form} />

      <ControlledInput
        name="customer.finances.0.annualIncome"
        label={getMiddleText('ADHERENT_REVENU_ANNUEL_LABEL')}
        form={form}
        displayErrorMessage={true}
        rules={
          equals(getMiddleText('ADHERENT_REVENU_ANNUEL_UNITE'), 'PASS')
            ? numberIsBetween(
              getMiddleNumber('ADHERENT_REVENU_ANNUEL_MIN') *
                  propOr(0, 'PASS', legalIndicators),
              getMiddleNumber('ADHERENT_REVENU_ANNUEL_MAX') *
                  propOr(0, 'PASS', legalIndicators),
              getMiddleText('ADHERENT_REVENU_ANNUEL_MESSAGE_ERREUR', {
                minAnnualRevenue: getMiddleNumber(
                  'ADHERENT_REVENU_ANNUEL_MIN'
                ),
                maxAnnualRevenue: getMiddleNumber(
                  'ADHERENT_REVENU_ANNUEL_MAX'
                ),
                annualRevenueUnit: getMiddleText(
                  'ADHERENT_REVENU_ANNUEL_UNITE'
                )
              }),
              getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_OBLIGATOIRE')
            )
            : isRequiredInput(
              getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_OBLIGATOIRE')
            )
        }
        disabled={and(
          getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_OBLIGATOIRE'),
          getMiddleBoolean(
            'ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_CONTRAT'
          )
        )}
        append="€"
        data-testid="ADHERENT_REVENU_ANNUEL"
        isDisplayed={
          getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />

      {getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_NECESSAIRE') &&
        getMiddleBoolean(
          'ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_CONTRAT'
        ) && (
        <div className="mt-2 col-span-2">
          <Message
            variant="info"
            className="mt-2 text-sm w-full py-2"
            label={getMiddleText('ADHERENT_REVENU_ANNUEL_MESSAGE_INFO')}
          />
        </div>
      )}

      <SouscriptionCustomerGranteesExtraForm
        form={form}
        legalIndicators={legalIndicators}
      />

      <ControlledRadioButton
        label={getMiddleText('ADHERENT_TELETRANSMISSION_QUESTION', {
          clientName: getMiddleText('GENERAL_CLIENT_NOM')
        })}
        name={'customer.socialSecurities.0.isTeletransmission'}
        id={'customer.socialSecurities.0.isTeletransmission'}
        form={form}
        rules={{
          ...isRequiredBoolean(
            getMiddleBoolean('ADHERENT_TELETRANSMISSION_EST_OBLIGATOIRE')
          )
        }}
        options={booleanOptions}
        booleanValue={getMiddleBoolean('ADHERENT_TELETRANSMISSION_REPONSE')}
        data-testid="ADHERENT_TELETRANSMISSION_REPONSE"
        message={getMiddleText('ADHERENT_TELETRANSMISSION_MESSAGE_INFO')}
        isDisplayed={getMiddleBoolean(
          'ADHERENT_TELETRANSMISSION_EST_NECESSAIRE'
        )}
      />

      <ControlledRadioButton
        label={getMiddleText('ADHERENT_OPT_NUM_QUESTION')}
        id="customer.isDigitalContract"
        name="customer.isDigitalContract"
        form={form}
        rules={isRequiredBoolean(
          getMiddleBoolean('ADHERENT_OPT_NUM_EST_OBLIGATOIRE')
        )}
        options={isDigitalContractOptions}
        booleanValue
        message={getMiddleText('ADHERENT_OPT_NUM_MESSAGE_INFO')}
        data-testid="ADHERENT_OPT_NUM_QUESTION"
        isDisplayed={getMiddleBoolean('ADHERENT_OPT_NUM_EST_NECESSAIRE')}
      />

      <SouscriptionCustomerExtraForm project={project} form={form} />
    </FormLayout>
  );
};

SouscriptionCustomerForm.propTypes = {
  getMiddleBoolean: PropTypes.func,
  getMiddleNumber: PropTypes.func,
  professionalSituations: PropTypes.array,
  socioProfessionalCategories: PropTypes.array,
  activities: PropTypes.array,
  regimeObligatoire: PropTypes.object,
  jobs: PropTypes.array,
  selectedTab: PropTypes.number,
  legalIndicators: PropTypes.object,
  familySituations: PropTypes.array,
  getMiddleText: PropTypes.func,
  getCountries: PropTypes.object,
  madelinMonths: PropTypes.array
};

export default SouscriptionCustomerForm;
